import { useState, useEffect } from "react";
import { CheckCircle, Warning, Coins, Spinner, ArrowSquareOut } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { fetchQuotePayments } from "@/lib/api";

/**
 * QuotePaymentChip
 * ------------------
 * Tiny header pill rendered next to the invoice number on QuoteDetailPage.
 * Surfaces the aggregated payment status so reps know "paid / partial /
 * unpaid" without leaving the quote. Tap to drill into the customer's
 * full Journal Entries panel.
 *
 * Hidden when the backend reports `payment_status="none"` (no journal
 * entries reference this quote yet — typical for fresh quotes).
 */
export default function QuotePaymentChip({ headingId, magicToken = "", customerId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!headingId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchQuotePayments(headingId, magicToken ? { t: magicToken } : {})
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { /* silent — chip just won't render */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [headingId, magicToken]);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 ml-1" data-testid="payment-chip-loading">
        <Spinner size={11} className="animate-spin" />
      </span>
    );
  }
  if (!data || data.payment_status === "none") return null;

  const fmt = (n) =>
    `$${Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const dt = data.last_payment_at ? new Date(data.last_payment_at) : null;
  const lastDate = dt ? dt.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : null;

  const config = {
    paid:    { label: "Paid",    icon: CheckCircle, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", iconCls: "text-emerald-600" },
    partial: { label: "Partial", icon: Coins,       cls: "bg-amber-50 text-amber-800 border-amber-300",       iconCls: "text-amber-600" },
    unpaid:  { label: "Unpaid",  icon: Warning,     cls: "bg-rose-50 text-rose-700 border-rose-200",          iconCls: "text-rose-600" },
  }[data.payment_status] || null;
  if (!config) return null;
  const Icon = config.icon;

  // Tooltip: full breakdown.
  const title = [
    `Total billed: ${fmt(data.total_due)}`,
    `Paid: ${fmt(data.total_credit)}`,
    data.balance > 0 ? `Balance: ${fmt(data.balance)}` : null,
    lastDate ? `Last payment: ${lastDate}` : null,
    `${data.count} journal ${data.count === 1 ? "entry" : "entries"} — click to view all`,
  ].filter(Boolean).join("\n");

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (customerId) navigate(`/customers/${encodeURIComponent(customerId)}`);
  };
  const clickable = !!customerId;

  return (
    <button
      type="button"
      data-testid={`payment-chip-${data.payment_status}`}
      onClick={onClick}
      disabled={!clickable}
      title={title}
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ml-1 transition-colors ${config.cls} ${clickable ? "hover:brightness-105 cursor-pointer" : "cursor-default"}`}
    >
      <Icon size={11} weight="fill" className={config.iconCls} />
      <span>{config.label}</span>
      {dt && <span className="hidden sm:inline opacity-70 normal-case tracking-normal">· {lastDate}</span>}
      {data.balance > 0 && data.payment_status !== "paid" && (
        <span className="hidden md:inline font-mono opacity-90 normal-case tracking-normal">· {fmt(data.balance)} due</span>
      )}
      {clickable && <ArrowSquareOut size={9} weight="bold" className="opacity-60" />}
    </button>
  );
}
